import React, { useState, useRef, useEffect } from 'react';
import { compressImage } from './compressorUtils';
import ImageUploader from '../../components/ImageUploader/ImageUploader';
import './Compressor.css';

const Compressor = () => {
  const [originalImage, setOriginalImage] = useState(null);
  const [compressedImage, setCompressedImage] = useState(null);
  const [quality, setQuality] = useState(80);
  const [isCompressing, setIsCompressing] = useState(false);
  const [originalSize, setOriginalSize] = useState(0);
  const [compressedSize, setCompressedSize] = useState(0);
  const [previewWidth, setPreviewWidth] = useState("100%");
  const [showTooltip, setShowTooltip] = useState(false);
  const canvasRef = useRef(null);

  // Adjust preview size for better comparison on smaller screens
  useEffect(() => {
    const handleResize = () => {
      setPreviewWidth(window.innerWidth <= 768 ? "100%" : "auto");
    };
    
    window.addEventListener('resize', handleResize);
    handleResize();
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleImageSelected = (img) => {
    // Reset states
    setCompressedImage(null);
    setCompressedSize(0);
    setOriginalImage(img);
    setOriginalSize(estimateImageSize(img));
  };

  const estimateImageSize = (img) => {
    // Estimate size from data URL if available
    if (img.src && img.src.startsWith('data:')) {
      const base64Data = img.src.split(',')[1];
      return base64Data ? atob(base64Data).length : 0;
    }
    // Fallback size estimation based on dimensions and pixel data
    return img.width * img.height * 4; // Rough RGBA byte estimation
  };

  const handleCompress = async () => {
    if (!originalImage) return;
    
    setIsCompressing(true);
    try {
      const result = await compressImage(originalImage, quality, canvasRef.current);
      setCompressedImage(result.url);
      setCompressedSize(result.size);
    } catch (error) {
      console.error('Error compressing image:', error);
      alert('Error compressing image. Please try again.');
    } finally {
      setIsCompressing(false);
    }
  };

  const handleDownload = () => {
    if (!compressedImage) return;
    
    const link = document.createElement('a');
    link.href = compressedImage;
    link.download = 'compressed-image.jpg';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const calculateReduction = () => {
    if (compressedSize >= originalSize) {
      return "0.00";
    }
    return ((1 - (compressedSize / originalSize)) * 100).toFixed(2);
  };
  
  // Get quality text indicator based on value
  const getQualityText = () => {
    if (quality >= 90) return "High";
    if (quality >= 70) return "Good";
    if (quality >= 50) return "Medium";
    if (quality >= 30) return "Low";
    return "Very Low";
  };

  return (
    <div className="compressor tool-container">
      <div className="container">
        <div className="tool-header">
          <h1>Image Compressor</h1>
          <p>Reduce the file size of your images while maintaining quality</p>
        </div>
        
        <div className="tool-content">
          <ImageUploader 
            onImageSelected={handleImageSelected}
            selectedImage={originalImage}
          />
          
          {originalImage && (
            <div className="compression-controls">
              <div className="control-group quality-slider">
                <div className="quality-label">
                  <label htmlFor="quality">Quality: </label>
                  <div 
                    className="quality-value" 
                    onMouseEnter={() => setShowTooltip(true)}
                    onMouseLeave={() => setShowTooltip(false)}
                  >
                    <span className="quality-number">{quality}%</span>
                    <span className="quality-text">({getQualityText()})</span>
                    
                    {showTooltip && (
                      <div className="tooltip">
                        Higher quality means larger file size. Lower quality means smaller file size but may affect image appearance.
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="range-container">
                  <input 
                    type="range" 
                    id="quality" 
                    min="1" 
                    max="100" 
                    value={quality} 
                    onChange={(e) => setQuality(parseInt(e.target.value))} 
                    className="quality-range"
                  />
                  <div className="range-labels">
                    <span>Low</span>
                    <span>Medium</span>
                    <span>High</span>
                  </div>
                </div>
              </div>
              
              <button 
                className={`btn compress-btn ${isCompressing ? 'compressing' : ''}`}
                onClick={handleCompress}
                disabled={isCompressing}
              >
                {isCompressing ? (
                  <>
                    <span className="spinner"></span>
                    <span>Compressing...</span>
                  </>
                ) : 'Compress Image'}
              </button>
            </div>
          )}
          
          {originalImage && (
            <div className="image-preview-section">
              <div className="image-preview-container">
                <h3>Original</h3>
                <div className="image-preview">
                  <div className="image-wrapper" style={{ maxWidth: previewWidth }}>
                    <img src={originalImage.src} alt="Original" />
                  </div>
                  <div className="image-info">
                    <p><strong>Size:</strong> {formatSize(originalSize)}</p>
                    <p><strong>Dimensions:</strong> {originalImage.width} x {originalImage.height}px</p>
                  </div>
                </div>
              </div>
              
              <div className="image-preview-container">
                <h3>Compressed</h3>
                {compressedImage ? (
                  <div className="image-preview">
                    <div className="image-wrapper" style={{ maxWidth: previewWidth }}>
                      <img src={compressedImage} alt="Compressed" />
                    </div>
                    <div className="image-info">
                      <p><strong>Size:</strong> {formatSize(compressedSize)}</p>
                      <p><strong>Reduction:</strong> <span className="reduction">{calculateReduction()}%</span></p>
                    </div>
                    <button className="btn btn-primary download-btn" onClick={handleDownload}>
                      <span className="download-icon">↓</span> Download Compressed Image
                    </button>
                  </div>
                ) : (
                  <div className="image-preview placeholder-preview">
                    <div className="image-wrapper empty-preview" style={{ maxWidth: previewWidth }}>
                      <p>Compress the image to see the result here</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Hidden canvas for compression */}
          <canvas ref={canvasRef} style={{ display: 'none' }}></canvas>
        </div>
      </div>
    </div>
  );
};

export default Compressor; 